import React, {useContext, useEffect, useState} from 'react';
import cn from 'classnames';
import {Integration_buttons} from '../integration_buttons/integration_buttons';
import {Integration_buttons_icons} from '../integration_buttons_icons/integration_buttons_icons';
import {Input} from '../../../../hola/pub/lib/input';
import {Button} from '../../../../hola/pub/lib/button';
import {Settings_context} from '../../../../hola/pub/settings_ctx';
import {
    i_integrations_heading_props,
    Integrations_heading,
    Integrations_heading_editor
} from './integrations_heading';
import styles from './integrations_hero.module.less';

export type t_integration_item = {
    id: string;
    category: string;
    name: string;
    icon: string;
    link: string;
};

export interface i_integrations_items extends i_integrations_heading_props {
    items: t_integration_item[];
}

export interface i_integrations_full_props {
    value: i_integrations_items;
    on_cancel: VoidFunction
    on_change?: (v: i_integrations_items) => void;
}

export const Integrations_hero_editor = (props:
    i_integrations_full_props)=>{
    const {value, on_cancel, on_change} = props;
    const [state, set_state] = useState<i_integrations_items>(()=>{
        return {
            title: value?.title?.trim() ?? '',
            title_tag: value?.title_tag?.trim() ?? '',
            items: value?.items?.length ? value.items : [],
        };
    });
    const [new_item, set_new_item] = useState<Partial<t_integration_item>>({});
    const update_item = (
        id: string,
        field: keyof t_integration_item,
        new_value: string
    )=>
    {
        set_state(prev=>({
            ...prev,
            items: prev.items.map(item=>
                item.id === id ? {...item, [field]: new_value} : item
            ),
        }));
    };
    const delete_item = (id: string)=>{
        set_state(prev=>({
            ...prev,
            items: prev.items.filter(item=>item.id !== id),
        }));
    };
    const add_item = ()=>{
        if (
            !new_item.name || !new_item.category ||
            !new_item.icon || !new_item.link
        )
        {
            return;
        }
        const id = new_item.name.toLowerCase().replace(/\s+/g, '_');
        set_state(prev=>({
            ...prev,
            items: [...prev.items, {...(new_item as t_integration_item), id}],
        }));
        set_new_item({});
    };
    return <>
      <Integrations_heading_editor
        on_cancel={on_cancel} value={value} on_change={on_change}/>
      <div style={{marginTop: 8}}>
        <Input
          size="sm"
          label="Name"
          value={new_item.name || ''}
          on_change={(name_value: string)=>
            set_new_item({...new_item, name: name_value})}
        />
        <Input
          size="sm"
          label="Category"
          value={new_item.category || ''}
          on_change={(category_value: string)=>
            set_new_item({...new_item, category: category_value})}
        />
        <Input
          size="sm"
          label="Icon URL"
          value={new_item.icon || ''}
          on_change={(icon_url_value: string)=>
            set_new_item({...new_item, icon: icon_url_value})}
        />
        <Input
          size="sm"
          label="Link"
          value={new_item.link || ''}
          on_change={(link_value: string)=>
            set_new_item({...new_item, link: link_value})}
        />
        <Button caption="Add" size="s" on_click={add_item} />
        {state?.items.map(item=>
          <div key={item.id} style={{marginTop: 8}}>
            <Input
              size="sm"
              label="Name"
              value={item.name}
              on_change={(name_value: string)=>
                update_item(item.id, 'name', name_value)}
            />
            <Input
              size="sm"
              label="Category"
              value={item.category}
              on_change={(category_value: string)=>
                update_item(item.id, 'category', category_value)}
            />
            <Input
              size="sm"
              label="Icon URL"
              value={item.icon}
              on_change={(icon_url_value: string)=>
                update_item(item.id, 'icon', icon_url_value)}
            />
            <Input
              size="sm"
              label="Link"
              value={item.link}
              on_change={(link_value: string)=>
                update_item(item.id, 'link', link_value)}
            />
            <div style={{marginTop: 8}}>
              <Button caption="Delete" size="s" on_click={()=>
              delete_item(item.id)} />
            </div>
          </div>
        )}
        <div style={{marginTop: 8, marginBottom: 8}}>
          <Button
            size="s"
            caption="Save"
            on_click={()=>on_change?.(state)}
          />
        </div>
      </div>
    </>;
};

export const Integrations_hero = (props: i_integrations_items)=>{
    const items = props?.items || [];
    const {theme} = useContext(Settings_context);
    const integration_button_groups = React.useMemo(()=>{
        const categories = Array.from(new Set(items.map(item=>item.category)));
        return categories.map(category=>({
            value: category,
            label: category.charAt(0).toUpperCase() + category.slice(1).replace(/_/g, ' ')
        }));
    }, [items]);
    const grouped_items = React.useMemo(()=>{
      return items.reduce((acc, item)=>{
          if (!acc[item.category])
              acc[item.category] = [];
          acc[item.category].push(item);
          return acc;
      }, {});
    }, [items]);

    const initial_active_category = integration_button_groups[0]?.value ?? '';
    const [active_category, set_active_category] =
        useState<string>(initial_active_category);
    const integration_buttons_icons_items = React.useMemo(()=>{
        if (!active_category || !grouped_items[active_category])
            return [];
        return grouped_items[active_category].map(item=>({
            id: item.id,
            img: item.icon,
            name: item.name,
            link: item.link,
            category: item.category,
        }));
    }, [active_category, grouped_items]);
    useEffect(()=>{
        if (initial_active_category && !active_category)
            set_active_category(initial_active_category);
    }, [initial_active_category, active_category]);

    return <div className={styles.hero}>
      <Integrations_heading title={props.title} title_tag={props.title_tag}/>
      <div className={cn(theme.container, styles.groups_block)}>
        <Integration_buttons
          groups={integration_button_groups}
          active_category={active_category}
          on_category_change={set_active_category}
        />
        <Integration_buttons_icons
          items={integration_buttons_icons_items}
        />
      </div>
    </div>;
};

